/**
 * EntityAuditSection — collapsed-by-default audit trail for a single entity,
 * embedded at the bottom of detail pages (invoice, customer, …).
 *
 * Phase 13 Cluster A — Feature D.
 */
import { useState } from 'react';
import type { AuditEntityType } from '@armard/shared';
import { useAuditForEntity } from '../../hooks/useAuditLog';
import { GlassPanel, LoadingSpinner } from '../ui';
import { AuditTimeline } from './AuditTimeline';

interface EntityAuditSectionProps {
  entityType: AuditEntityType;
  entityId: string;
  title?: string;
  /** Start expanded. Default: false (collapsed). */
  defaultOpen?: boolean;
}

export function EntityAuditSection({
  entityType,
  entityId,
  title = 'Audit-Protokoll',
  defaultOpen = false,
}: EntityAuditSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const { data, isLoading, error } = useAuditForEntity(entityType, entityId, { limit: 50 }, open);

  return (
    <GlassPanel className="p-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <h3 className="text-lg font-medium text-white">{title}</h3>
        <span className="text-white/40 text-sm">
          {open ? '▾ Einklappen' : `▸ ${data?.total ? `${data.total} Einträge` : 'Anzeigen'}`}
        </span>
      </button>

      {open && (
        <div className="mt-4">
          {isLoading ? (
            <div className="flex justify-center py-6">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <p className="text-red-400 text-sm text-center py-4">
              Audit-Protokoll konnte nicht geladen werden.
            </p>
          ) : (
            <AuditTimeline
              entries={data?.entries ?? []}
              emptyMessage="Für diese Ressource sind noch keine Änderungen protokolliert."
            />
          )}
        </div>
      )}
    </GlassPanel>
  );
}
